import type { FieldEntryDTO, SectionFieldCollection, SectionFieldDTO } from '../magic-report.type';
import { SCHEDULE_SECTION_CONFIG, NOTARY_SECTION_CONFIG, DOCUMENTS_RECEIVED_SECTION_CONFIG } from './sections.config';
import type { FieldConfig } from './DeclarativeLayoutRenderer';
import { formatDateWithOrdinal, formatCurrency, formatISODate } from '../../../../common/utils/common.util';
import { generateG703IntroText, generateG703DetailText } from './StoredMaterialsSection';
import type { StoredMaterialRow } from './StoredMaterialsTable';
import { roundRecommendedPercent } from './ReductionCommentsTable';
import { collectDrawScopedSections } from './draw-scoped-sections.util';
import { filterVisibleCoLienDraws, isCoLienHidden, resolveHiddenCoLienDrawIds } from './co-lien-visibility.util';
import { filterAdjustmentsAboveThreshold, formatSignedCurrency, resolveMinorAdjustmentsThreshold } from './minor-adjustments.util';
import { allTestsNeverReceived, parseMaterialTestRows } from './material-testing.util';
import { REPORT_FONT_FAMILY, REPORT_TABLE_CLASS, REPORT_TABLE_FONT_SIZE, REPORT_TABLE_STYLE } from '../../shared/utils/reportTypography';

/** Minimal shape for Reduction Comments table row when building action items */
interface ReductionRowParsed {
  description?: string;
  requestedAmount?: number;
  percentRequested?: number;
  percentRecommended?: number;
  fundingAmount?: number;
  reducedBy?: number;
  conditionType?: 'no-work' | 'partial' | string;
}

/** Minimal shape for Reallocations table row when building action items */
interface ReallocationRowParsed {
  description?: string;
  originalBudget?: number;
  totalReallocation?: number;
  percentChange?: number;
  revisedBudget?: number;
  currentReallocation?: number;
  /** Which covered draw (G702) the row belongs to; absent on rows saved before multi-draw. */
  drawId?: string;
}

/** Minimal shape for Change Orders table row when building action items */
interface ChangeOrderRowParsed {
  coNumber?: string;
  description?: string;
  doWeHaveDoc?: 'Yes' | 'No' | string;
  contractor?: 'Yes' | 'No' | string;
  architect?: 'Yes' | 'No' | string;
  owner?: 'Yes' | 'No' | string;
  prevCO?: number;
  currCO?: number;
  isLastMonth?: boolean;
  /** Which covered draw the row belongs to; absent on rows saved before multi-draw. */
  drawId?: string;
}

const LARGE_ORDER_FALLBACK = 1_000_000_000;

const escapeHtml = (value: string) =>
  value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#039;');

const toParagraphHtml = (value?: string) => {
  const v = (value || '').trim();
  if (!v || v.toLowerCase() === 'n/a') return '';
  return `<p style="margin: 0 0 1em 0; text-align: justify;">${escapeHtml(v).replace(/\n/g, '<br />')}</p>`;
};

/**
 * Get all fields from section config in layout order
 */
const getAllFieldsInOrder = (sectionId: string): FieldConfig[] => {
  let config: any = null;
  if (sectionId === 'schedule') {
    config = SCHEDULE_SECTION_CONFIG;
  } else if (sectionId === 'notaryAndSignatures' || sectionId === 'notary') {
    config = NOTARY_SECTION_CONFIG;
  }
  // Add more sections as needed

  if (!config?.layout) return [];

  // Return fields in layout order: ConstructionProgressScheduleText first (rendered above columns), then leftColumn, rightColumn, bottomSection, singleColumn
  const constructionProgressField: FieldConfig[] =
    sectionId === 'schedule'
      ? [{ fieldName: 'ConstructionProgressScheduleText', type: 'text', label: 'Construction Progress Schedule' }]
      : [];
  return [
    ...constructionProgressField,
    ...(config.layout.leftColumn || []),
    ...(config.layout.rightColumn || []),
    ...(config.layout.bottomSection || []),
    ...(config.layout.singleColumn || []),
  ];
};

/**
 * Get field position index in section layout (for ordering)
 * Returns -1 if field not found
 */
const getFieldPositionIndex = (sectionId: string, fieldName: string): number => {
  const allFields = getAllFieldsInOrder(sectionId);
  const index = allFields.findIndex(f => f.fieldName === fieldName);
  return index;
};

/**
 * Get field label from section config by fieldName
 */
const getFieldLabel = (sectionId: string, fieldName: string): string => {
  const allFields = getAllFieldsInOrder(sectionId);
  const fieldConfig = allFields.find(f => f.fieldName === fieldName);
  return fieldConfig?.label || formatFieldName(fieldName);
};

/**
 * Format field name as fallback (camelCase to Title Case)
 */
const formatFieldName = (fieldName: string): string => {
  return fieldName
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, str => str.toUpperCase())
    .trim();
};

/**
 * Format field value based on field type
 */
const formatFieldValue = (field: SectionFieldDTO, fieldConfig?: FieldConfig): string => {
  const value = field.value || '';
  const fieldType = field.fieldType;

  // Get type from config if available, otherwise use field.fieldType
  const type = fieldConfig?.type;

  if (type === 'checkbox') {
    return value === 'true' ? 'Yes' : 'No';
  }

  if (type === 'date' || fieldType === 5) {
    // FieldType.Date = 5
    return formatDateWithOrdinal(value);
  }

  if (type === 'number' || fieldType === 6) {
    // FieldType.Number = 6
    return value || '';
  }

  if (type === 'select' || fieldType === 1) {
    // FieldType.Select = 1
    return value || '';
  }

  if (type === 'readonly' || type === 'readonly-calculated') {
    return value || '';
  }

  // Default: text fields
  return value || '';
};

// Sections whose bookmarks are limited to a subset of their fields (see CompleteSectionConfig.actionItemFields).
const ACTION_ITEM_FIELDS_BY_SECTION: Record<string, readonly string[] | undefined> = {
  schedule: SCHEDULE_SECTION_CONFIG.actionItemFields,
};

/** False when the field's section limits bookmarks to its actionItemFields and this field isn't one of them. */
export function canBookmarkField(sectionKey: string, fieldName: string): boolean {
  const allowed = ACTION_ITEM_FIELDS_BY_SECTION[sectionKey];
  return !allowed || allowed.includes(fieldName);
}

/**
 * Computes the display rank (1-based) for a given sectionId.
 * Rank = position among all active-bookmarked sections sorted by their actionItemOrder.
 * Returns undefined if this section has no active bookmarks.
 */
export function getSectionRank(sections: any, sectionId: string): number | undefined {
  if (!sections) return undefined;

  const getOrder = (collection?: SectionFieldCollection, sectionKey = ''): number | undefined => {
    if (!collection?.fields) return undefined;
    for (const f of collection.fields) {
      if (!canBookmarkField(sectionKey, f.fieldName)) continue;
      if (f.addToActionItems && typeof f.actionItemOrder === 'number') return f.actionItemOrder;
      for (const e of f.entries || []) {
        if (e.addToActionItems && typeof e.actionItemOrder === 'number') return e.actionItemOrder;
      }
    }
    return undefined;
  };

  // Normalise sections so each logical section appears exactly once, keyed by sectionId form.
  // Some sections (application-comments, reduction-comments, previous-fundings) exist under both
  // a kebab key and a camelCase key in state. Merge them by checking both forms.
  const toCamel = (k: string) => k.replace(/-([a-z])/g, (_, c) => c.toUpperCase());
  const normalised: Record<string, any> = {};
  Object.entries(sections).forEach(([key, value]: [string, any]) => {
    if (!value) return;
    const camel = toCamel(key);
    // Prefer the key that already exists; if both present, the one with bookmarked data wins
    if (normalised[camel] === undefined || getOrder(value, camel) !== undefined) {
      normalised[camel] = value;
    }
  });

  const ranked: { camelKey: string; order: number }[] = [];
  Object.entries(normalised).forEach(([camelKey, value]: [string, any]) => {
    if (!value) return;
    if (Array.isArray(value)) {
      value.forEach((item: any, idx: number) => {
        const order = getOrder(item);
        if (order !== undefined) ranked.push({ camelKey: `${camelKey}[${idx}]`, order });
      });
      return;
    }
    const order = getOrder(value, camelKey);
    if (order !== undefined) ranked.push({ camelKey, order });
  });

  ranked.sort((a, b) => a.order - b.order);
  const sectionIdCamel = toCamel(sectionId);
  const idx = ranked.findIndex(r => r.camelKey === sectionIdCamel);
  return idx === -1 ? undefined : idx + 1;
}

/**
 * Returns the next available actionItemOrder — one higher than the current max across all sections.
 * Used exclusively by onBook() in MagicReportSection and DynamicSectionItem.
 */
export function getNextActionItemOrder(sections: any): number {
  let max = 0;
  const visit = (collection?: SectionFieldCollection) => {
    if (!collection?.fields) return;
    collection.fields.forEach((f: SectionFieldDTO) => {
      if (typeof f.actionItemOrder === 'number') max = Math.max(max, f.actionItemOrder);
      (f.entries || []).forEach((e: FieldEntryDTO) => {
        if (typeof e.actionItemOrder === 'number') max = Math.max(max, e.actionItemOrder);
      });
    });
  };

  if (!sections) return 1;

  Object.values(sections).forEach((value: any) => {
    if (!value) return;
    if (Array.isArray(value)) {
      value.forEach(v => visit(v));
      return;
    }
    if (value?.fields) visit(value);
  });

  return max + 1;
}

export interface BookmarkedSection {
  /** Section key used in getSectionRank (e.g. 'applicationComments', 'dynamicSections[0]') */
  sectionKey: string;
  /** Human-readable label shown in the reorder modal */
  label: string;
  /** Lowest actionItemOrder found in this section — determines its current rank */
  minOrder: number;
}

/**
 * Collects all sections that have at least one active bookmark, sorted by their current rank.
 * Used by the reorder modal to show the current section order.
 */
export function getBookmarkedSections(sections: any, sectionLabels: Record<string, string>): BookmarkedSection[] {
  if (!sections) return [];

  const getMinOrder = (collection: any, sectionKey = ''): number | undefined => {
    if (!collection?.fields) return undefined;
    let min: number | undefined;
    for (const f of collection.fields) {
      if (!canBookmarkField(sectionKey, f.fieldName)) continue;
      if (f.addToActionItems && typeof f.actionItemOrder === 'number') {
        min = min === undefined ? f.actionItemOrder : Math.min(min, f.actionItemOrder);
      }
      for (const e of f.entries || []) {
        if (e.addToActionItems && typeof e.actionItemOrder === 'number') {
          min = min === undefined ? e.actionItemOrder : Math.min(min, e.actionItemOrder);
        }
      }
    }
    return min;
  };

  // Normalise sections so each logical section appears once, keyed by its kebab sectionId form.
  // After page load, sections like 'applicationComments' and 'application-comments' both exist.
  // Merge them: prefer whichever key has bookmarked data; use the kebab key as sectionKey since
  // that is the sectionId MagicReportSection writes under.
  const toCamel = (k: string) => k.replace(/-([a-z])/g, (_, c) => c.toUpperCase());
  const toKebab = (k: string) => k.replace(/([A-Z])/g, c => `-${c.toLowerCase()}`);

  // Build a map: kebabKey → { value, sectionKey (kebab if exists, else key as-is) }
  const normalised = new Map<string, { value: any; sectionKey: string }>();
  Object.entries(sections).forEach(([key, value]: [string, any]) => {
    if (!value) return;
    if (Array.isArray(value)) return; // handled separately below
    const kebabKey = key.includes('-') ? key : toKebab(key);
    const existing = normalised.get(kebabKey);
    if (!existing || getMinOrder(value, toCamel(kebabKey)) !== undefined) {
      normalised.set(kebabKey, { value, sectionKey: key.includes('-') ? key : (existing?.sectionKey ?? key) });
    }
  });

  const result: BookmarkedSection[] = [];

  // Dynamic (array) sections first — no dedup needed
  Object.entries(sections).forEach(([key, value]: [string, any]) => {
    if (!value || !Array.isArray(value)) return;
    value.forEach((item: any, idx: number) => {
      const sectionKey = `${key}[${idx}]`;
      const minOrder = getMinOrder(item);
      if (minOrder !== undefined) {
        const headerField = item?.fields?.find((f: any) => f.fieldName === 'header');
        const label = sectionLabels[sectionKey] || headerField?.value || sectionKey;
        result.push({ sectionKey, label, minOrder });
      }
    });
  });

  // Static sections from normalised map
  normalised.forEach(({ value, sectionKey }) => {
    const minOrder = getMinOrder(value, toCamel(sectionKey));
    if (minOrder !== undefined) {
      result.push({ sectionKey, label: sectionLabels[sectionKey] || sectionLabels[toCamel(sectionKey)] || sectionKey, minOrder });
    }
  });

  result.sort((a, b) => a.minOrder - b.minOrder);
  return result;
}

/**
 * Reassigns actionItemOrder values in sections to reflect the given new section order.
 *
 * Each section in newOrder gets a base of (newIndex + 1) * 10 (i.e. 10, 20, 30, …), preserving
 * up to 9 intra-section field slots before the next section's range begins. After calling this
 * the caller MUST reset the shared order counter so claimActionItemOrder re-seeds above the new max.
 */
export function reorderSectionActionItems(sections: any, newOrder: BookmarkedSection[]): any {
  if (!sections || !newOrder.length) return sections;

  // Bookmarked sections get bases 10, 20, 30, … (steps of 10 preserve intra-section slot order).
  // Unbookmarked sections that happen to have actionItemOrder values are placed above the highest
  // bookmarked base so their values never interleave with a reordered section's new range.
  const highestBase = newOrder.length * 10;
  const unbookmarkedBase = highestBase + 10;

  const offsets = new Map<string, number>();
  newOrder.forEach((section, newIndex) => {
    const newBase = (newIndex + 1) * 10;
    offsets.set(section.sectionKey, newBase - section.minOrder);
  });

  const shiftCollection = (sectionKey: string, collection: any): any => {
    if (!collection?.fields) return collection;

    if (offsets.has(sectionKey)) {
      const offset = offsets.get(sectionKey)!;
      return {
        ...collection,
        fields: collection.fields.map((f: SectionFieldDTO) => ({
          ...f,
          actionItemOrder: typeof f.actionItemOrder === 'number' ? f.actionItemOrder + offset : f.actionItemOrder,
          entries: (f.entries || []).map((e: FieldEntryDTO) => ({
            ...e,
            actionItemOrder: typeof e.actionItemOrder === 'number' ? e.actionItemOrder + offset : e.actionItemOrder,
          })),
        })),
      };
    }

    // Section is not in the reorder list. If it has any actionItemOrder values that fall within
    // the bookmarked range [1..highestBase+9], shift them above to avoid interleaving.
    const needsShift = collection.fields.some(
      (f: SectionFieldDTO) =>
        (typeof f.actionItemOrder === 'number' && f.actionItemOrder <= highestBase + 9) ||
        (f.entries || []).some((e: FieldEntryDTO) => typeof e.actionItemOrder === 'number' && e.actionItemOrder <= highestBase + 9),
    );
    if (!needsShift) return collection;

    const minUnbookmarked = collection.fields.reduce((min: number, f: SectionFieldDTO) => {
      if (typeof f.actionItemOrder === 'number') min = Math.min(min, f.actionItemOrder);
      (f.entries || []).forEach((e: FieldEntryDTO) => {
        if (typeof e.actionItemOrder === 'number') min = Math.min(min, e.actionItemOrder);
      });
      return min;
    }, Infinity);

    const unbookmarkedOffset = minUnbookmarked === Infinity ? 0 : unbookmarkedBase - minUnbookmarked;

    return {
      ...collection,
      fields: collection.fields.map((f: SectionFieldDTO) => ({
        ...f,
        actionItemOrder: typeof f.actionItemOrder === 'number' ? f.actionItemOrder + unbookmarkedOffset : f.actionItemOrder,
        entries: (f.entries || []).map((e: FieldEntryDTO) => ({
          ...e,
          actionItemOrder: typeof e.actionItemOrder === 'number' ? e.actionItemOrder + unbookmarkedOffset : e.actionItemOrder,
        })),
      })),
    };
  };

  const updated: any = {};
  Object.entries(sections).forEach(([key, value]: [string, any]) => {
    if (!value) {
      updated[key] = value;
      return;
    }
    if (Array.isArray(value)) {
      updated[key] = value.map((item: any, idx: number) => shiftCollection(`${key}[${idx}]`, item));
      return;
    }
    updated[key] = shiftCollection(key, value);
  });

  return updated;
}

/**
 * Magic Report Action Items HTML derived from bookmarked fields.
 *
 * Notary & Signatures: Multiple bookmarked field values render as paragraphs inside that single bullet
 * Schedule: Each bookmarked field renders as "Label: Value" on its own line
 */
export function buildMagicActionItemsHtml(
  sections: any,
  draws?: Array<{ drawId: string; drawSummary?: { name?: string }; budgetSummary?: { adjustmentsByCDCC?: number } }>,
  previousFundingsComment?: string,
  /**
   * Draws whose Change Orders and Lien Waivers sections the report hides. Their change orders are
   * left out of the bookmarked table and summary below; the lien-waiver bullets are comment text
   * that the section already generated from the visible payapps only.
   */
  hiddenCoLienDrawIds?: string[],
): string {
  const coLienHidden = resolveHiddenCoLienDrawIds(
    hiddenCoLienDrawIds,
    (draws || []).map(d => d.drawId),
  );
  // Draw order and names for the draw-scoped sections (Application / Reduction Comments), so their
  // per-draw copies print in report order and are labelled when more than one contributes.
  const drawOrder = draws?.map(d => d.drawId);
  const drawLabels: Record<string, string> | undefined = draws?.reduce(
    (acc, d) => {
      acc[d.drawId] = d.drawSummary?.name || '';
      return acc;
    },
    {} as Record<string, string>,
  );
  const drawLabelHtml = (label: string) => `<p style="margin: 0.25em 0 0.25em 0;"><strong><em>${escapeHtml(label)}</em></strong></p>`;

  const baseWrapperStart = `<div class="action-items" style="font-family: ${REPORT_FONT_FAMILY}; font-size: 23px;">`;
  const header = `<h3 class="action-items-heading" style="font-weight: bold; margin-bottom: 0.5rem; font-size: 23px !important;">1.2. Action Items</h3>`;
  const intro = `<p>Following is a summary of questions, comments, needs, or suggestions we have concerning our observations made at the site and our review of the Application for Payment and associated documentation.</p>`;

  // Notary & Signatures: paragraphs in one bullet
  const notary: SectionFieldCollection | undefined = sections?.notaryAndSignatures;
  const notaryTitle = 'Notary and Signatures';
  let notaryOrder: number | undefined;

  /** Notary selection-state fields (Signed/Notarized, Signed/Architect Reviewed, etc.) – excluded from action items; only textbox content is shown */
  const NOTARY_SELECTION_STATE_FIELDS = ['ContractorSignature', 'ArchEngSignature'];

  const notaryItems: Array<{ layoutOrder: number; actionItemOrder: number; html: string; isTextField: boolean }> = [];
  if (notary?.fields?.length) {
    const allNotaryFields = getAllFieldsInOrder('notaryAndSignatures');
    // Comments always come last
    const commentsPosition = allNotaryFields.length;

    notary.fields.forEach((f: SectionFieldDTO) => {
      if (f.fieldName === 'Comments') {
        (f.entries || [])
          .filter(e => e.addToActionItems)
          .forEach(e => {
            const html = toParagraphHtml(e.value);
            if (!html) return;
            notaryItems.push({
              layoutOrder: commentsPosition,
              actionItemOrder: e.actionItemOrder ?? LARGE_ORDER_FALLBACK + (e.order ?? 0),
              html,
              isTextField: false, // Comments are not text fields for spacing purposes
            });
            notaryOrder ??= e.actionItemOrder;
          });
        return;
      }

      // Only include textbox content; skip selection-state fields (Signed/Notarized, Architect Reviewed, etc.)
      if (NOTARY_SELECTION_STATE_FIELDS.includes(f.fieldName)) return;
      if (!f.addToActionItems) return;
      const html = toParagraphHtml(f.value);
      if (!html) return;
      const layoutOrder = getFieldPositionIndex('notaryAndSignatures', f.fieldName);
      const fieldConfig = allNotaryFields.find(fc => fc.fieldName === f.fieldName);
      // Check if this is a text field (FieldType.Text = 3 or type === 'text' or 'text-paired')
      const isTextField = f.fieldType === 3 || fieldConfig?.type === 'text' || fieldConfig?.type === 'text-paired';

      notaryItems.push({
        layoutOrder: layoutOrder >= 0 ? layoutOrder : LARGE_ORDER_FALLBACK,
        actionItemOrder: f.actionItemOrder ?? 0,
        html,
        isTextField,
      });
      notaryOrder ??= f.actionItemOrder;
    });
  }

  // Sort by layout order first, then by actionItemOrder for items at the same position
  notaryItems.sort((a, b) => {
    if (a.layoutOrder !== b.layoutOrder) {
      return a.layoutOrder - b.layoutOrder;
    }
    return a.actionItemOrder - b.actionItemOrder;
  });

  // Add extra spacing above text fields (FieldType.Text = 3)
  const notaryHtml = notaryItems
    .map((item, index) => {
      // Add small single-line spacing above text fields, but not if it's the first item or previous item was also a text field
      if (item.isTextField && index > 0 && !notaryItems[index - 1].isTextField) {
        // Use a div with minimal height instead of paragraph to avoid large spacing
        return `<div style="margin: 0; height: 0.5em;"></div>${item.html}`;
      }
      return item.html;
    })
    .join('');

  // Schedule: "Label: Value" lines
  const schedule: SectionFieldCollection | undefined = sections?.schedule;
  const scheduleTitle = 'Schedule';
  let scheduleOrder: number | undefined;
  const KEY_DATE_FIELD_NAMES = [
    'CommencementDate',
    'OriginalCompletionDate',
    'RevisedCompletionDate',
    'CDCCEstimatedCompletionDate',
    'BankLoanExpirationDate',
  ];

  const scheduleItems: Array<{
    layoutOrder: number;
    actionItemOrder: number;
    html: string;
    isTextField: boolean;
    label?: string;
    value?: string;
  }> = [];
  if (schedule?.fields?.length) {
    // Get all field configs from Schedule section layout
    const allScheduleFields = getAllFieldsInOrder('schedule');
    // Comments always come last
    const commentsPosition = allScheduleFields.length;

    schedule.fields.forEach((f: SectionFieldDTO) => {
      // Comments entries are not shown in action items
      if (f.fieldName === 'Comments') return;
      // Stray bookmarks on icon-less fields (e.g. from workbook ingestion) are never emitted.
      if (!canBookmarkField('schedule', f.fieldName)) return;

      if (KEY_DATE_FIELD_NAMES.includes(f.fieldName)) {
        if (f.addToActionItems && f.actionItemOrder !== undefined) {
          scheduleOrder = scheduleOrder === undefined ? f.actionItemOrder : Math.min(scheduleOrder, f.actionItemOrder);
        }
        return;
      }

      if (!f.addToActionItems) return;

      const fieldConfig = allScheduleFields.find(fc => fc.fieldName === f.fieldName);
      const layoutOrder = getFieldPositionIndex('schedule', f.fieldName);
      const formattedValue = formatFieldValue(f, fieldConfig);
      if (!formattedValue) return;

      // Check if this is a text field (FieldType.Text = 3 or type === 'text')
      const isTextField = f.fieldType === 3 || fieldConfig?.type === 'text' || fieldConfig?.type === 'text-paired';

      // Schedule Section Text and Construction Progress Schedule Text should render without label, just the value
      if (f.fieldName === 'ScheduledSectionText' || f.fieldName === 'ConstructionProgressScheduleText') {
        // Text fields use <p> tag with minimal margin
        const html = `<p style="margin: 0 0 0.5em 0;">${escapeHtml(formattedValue).replace(/\n/g, '<br />')}</p>`;
        scheduleItems.push({
          layoutOrder: layoutOrder >= 0 ? layoutOrder : LARGE_ORDER_FALLBACK,
          actionItemOrder: f.actionItemOrder ?? 0,
          html,
          isTextField,
        });
        scheduleOrder ??= f.actionItemOrder;
      } else {
        const label = fieldConfig?.label || getFieldLabel('schedule', f.fieldName);
        // Store label and value separately for table rendering
        scheduleItems.push({
          layoutOrder: layoutOrder >= 0 ? layoutOrder : LARGE_ORDER_FALLBACK,
          actionItemOrder: f.actionItemOrder ?? 0,
          html: '', // Will be generated later
          isTextField,
          label,
          value: formattedValue,
        });
        scheduleOrder ??= f.actionItemOrder;
      }
    });
  }

  // Sort by layout order first, then by actionItemOrder for items at the same position
  scheduleItems.sort((a, b) => {
    if (a.layoutOrder !== b.layoutOrder) {
      return a.layoutOrder - b.layoutOrder;
    }
    return a.actionItemOrder - b.actionItemOrder;
  });

  // Group consecutive non-text items into tables, interleaved with text paragraphs, in layout order
  type RenderBlock =
    | { kind: 'text'; html: string; layoutOrder: number }
    | { kind: 'table-row'; label: string; value: string; layoutOrder: number };

  const renderBlocks: RenderBlock[] = [];
  scheduleItems.forEach(item => {
    if (item.isTextField) {
      renderBlocks.push({ kind: 'text', html: item.html, layoutOrder: item.layoutOrder });
    } else if (item.label && item.value) {
      renderBlocks.push({ kind: 'table-row', label: item.label, value: item.value, layoutOrder: item.layoutOrder });
    }
  });
  renderBlocks.sort((a, b) => a.layoutOrder - b.layoutOrder);

  const scheduleDateFields: { fieldName: string; label: string }[] = [
    { fieldName: 'CommencementDate', label: 'Commencement Date' },
    { fieldName: 'OriginalCompletionDate', label: 'Original Completion Date' },
    { fieldName: 'RevisedCompletionDate', label: 'Revised Completion Date' },
    { fieldName: 'CDCCEstimatedCompletionDate', label: 'CDCC Estimated Completion Date' },
    { fieldName: 'BankLoanExpirationDate', label: 'Bank Loan Expiration Date' },
  ];
  // Only include the key dates table when a date field itself is bookmarked. Bookmarking a comment
  // (Construction Progress Schedule / Schedule Section Text) must bring in that comment alone — it
  // previously dragged the whole dates table along with it.
  const includeDatesTable = schedule?.fields?.some(f => KEY_DATE_FIELD_NAMES.includes(f.fieldName) && f.addToActionItems) ?? false;

  const getScheduleFieldValue = (fieldName: string) => schedule?.fields?.find(f => f.fieldName === fieldName)?.value ?? '';
  const dateTableRows = scheduleDateFields
    .map(({ fieldName, label }) => {
      const raw = getScheduleFieldValue(fieldName);
      const formatted = raw ? formatISODate(raw, false) || raw : 'Not Provided';
      return `<tr><td style="border: none; padding: 4px 8px; font-family: ${REPORT_FONT_FAMILY}; font-size: ${REPORT_TABLE_FONT_SIZE};">${escapeHtml(label)}</td><td style="border: none; padding: 4px 8px; font-family: ${REPORT_FONT_FAMILY}; font-size: ${REPORT_TABLE_FONT_SIZE}; text-align: right;">${escapeHtml(formatted)}</td></tr>`;
    })
    .join('');

  const TABLE_WRAP = (rows: string) =>
    `<table class="${REPORT_TABLE_CLASS.SCHEDULE_DATES}" style="border-collapse: collapse; margin: 0; width: 100%; border: none; font-family: ${REPORT_FONT_FAMILY}; font-size: ${REPORT_TABLE_FONT_SIZE};"><colgroup><col style="width:60%" /><col style="width:40%" /></colgroup><tbody>${rows}</tbody></table>`;

  const buildTableRow = (label: string, value: string) =>
    `<tr><td style="border: none; padding: 4px 8px; font-family: ${REPORT_FONT_FAMILY}; font-size: ${REPORT_TABLE_FONT_SIZE};">${escapeHtml(label)}</td><td style="border: none; padding: 4px 8px; font-family: ${REPORT_FONT_FAMILY}; font-size: ${REPORT_TABLE_FONT_SIZE}; text-align: right;">${escapeHtml(value)}</td></tr>`;

  // If no ConstructionProgressScheduleText in renderBlocks, prepend dates table directly
  const hasScheduleTextBlock = renderBlocks.some(b => b.kind === 'text');

  // Collect all table-row blocks from renderBlocks
  const renderBlockTableRows = renderBlocks
    .filter((b): b is Extract<RenderBlock, { kind: 'table-row' }> => b.kind === 'table-row')
    .map(r => buildTableRow(r.label, r.value))
    .join('');

  // Build one unified table: key dates rows + all render-block table rows
  const allTableRows = (includeDatesTable ? dateTableRows : '') + renderBlockTableRows;
  const unifiedTableHtml = allTableRows ? TABLE_WRAP(allTableRows) : '';

  // Text blocks (ConstructionProgressScheduleText etc.) interleaved before the unified table
  const textBlocks = renderBlocks.filter((b): b is Extract<RenderBlock, { kind: 'text' }> => b.kind === 'text');

  let scheduleHtml = '';
  if (!hasScheduleTextBlock) {
    scheduleHtml = unifiedTableHtml;
  } else {
    textBlocks.forEach((block, idx) => {
      if (scheduleHtml) scheduleHtml += `<div style="margin: 0; height: 0.5em;"></div>`;
      scheduleHtml += block.html;
      // Insert unified table after the first text block
      if (idx === 0 && unifiedTableHtml) {
        scheduleHtml += `<div style="margin: 0; height: 0.5em;"></div>` + unifiedTableHtml;
      }
    });
  }

  // Application Comments: paragraphs for bookmarked TextArray entries (including Comments).
  // Draw-scoped — one copy per covered draw, concatenated with a draw label when more than one
  // draw has bookmarked content so the reader can tell which application each item belongs to.
  const applicationCommentsTitle = 'Application Comments';
  let applicationCommentsHtml = '';
  let applicationCommentsOrder: number | undefined;
  {
    const buildApplicationCommentsBody = (collection?: SectionFieldCollection): string => {
      if (!collection?.fields?.length) return '';
      const applicationParagraphs: string[] = [];
      collection.fields.forEach((f: SectionFieldDTO) => {
        // Handle all TextArray fields (MinorDiscrepancy, ShopDrawings, Deposit, Comments, etc.)
        if (f.fieldType === 4 /* FieldType.TextArray */ && f.entries?.length) {
          f.entries
            // Skip last-month entries; only include current-period items
            .filter(e => e.addToActionItems && !e.isFromLastMonth && (e.value || '').trim())
            .forEach(e => {
              const html = toParagraphHtml(e.value);
              if (!html) return;
              applicationParagraphs.push(html);
              applicationCommentsOrder ??= e.actionItemOrder;
            });
        }
      });
      return applicationParagraphs.join('');
    };

    const perDraw = collectDrawScopedSections(sections, 'application-comments', drawOrder);
    if (perDraw.length > 0) {
      const bodies = perDraw
        .map(({ drawId, collection }) => ({ label: drawLabels?.[drawId], body: buildApplicationCommentsBody(collection) }))
        .filter(entry => entry.body);
      const showLabels = bodies.length > 1;
      applicationCommentsHtml = bodies
        .map(entry => (showLabels && entry.label ? `${drawLabelHtml(entry.label)}${entry.body}` : entry.body))
        .join('');
    } else {
      // Legacy report saved before draw-scoping.
      applicationCommentsHtml = buildApplicationCommentsBody(
        (sections as any)?.['application-comments'] || (sections as any)?.applicationComments,
      );
    }
  }

  // Stored Materials: table (Description, Stored Offsite?, Amount) then G703 Comments and List Other Material Comments
  const storedMaterials: SectionFieldCollection | undefined = sections?.storedMaterials;
  const storedMaterialsTitle = 'Stored Materials';
  const STORED_ROWS_FIELD = 'StoredMaterialsRows';
  const G703_INTRO_FIELD = 'G703MaterialsIntroComment';
  const G703_FIELD = 'G703MaterialsComment';
  const OTHER_LIST_FIELD = 'OtherMaterialsList';

  let storedMaterialsHtml = '';
  let storedMaterialsOrder: number | undefined;
  if (storedMaterials?.fields?.length) {
    const rowsField = storedMaterials.fields.find((f: SectionFieldDTO) => f.fieldName === STORED_ROWS_FIELD);
    const g703IntroField = storedMaterials.fields.find((f: SectionFieldDTO) => f.fieldName === G703_INTRO_FIELD);
    const g703Field = storedMaterials.fields.find((f: SectionFieldDTO) => f.fieldName === G703_FIELD);
    const otherListField = storedMaterials.fields.find((f: SectionFieldDTO) => f.fieldName === OTHER_LIST_FIELD);
    const commentsField = storedMaterials.fields.find((f: SectionFieldDTO) => f.fieldName === 'Comments');

    const rows: StoredMaterialRow[] = (rowsField?.entries || [])
      .map((e: FieldEntryDTO) => {
        try {
          return JSON.parse(e.value || '') as StoredMaterialRow;
        } catch {
          return null;
        }
      })
      .filter((r): r is StoredMaterialRow => r != null);

    const tableStyle = REPORT_TABLE_STYLE;
    const thStyle = 'border: none; padding: 4px 8px; font-weight: bold; text-align: left;';
    const thAmountStyle = 'border: none; padding: 4px 8px; font-weight: bold; text-align: right;';
    const tdStyle = 'border: none; padding: 4px 8px;';
    const tdAmountStyle = 'border: none; padding: 4px 8px; text-align: right;';

    // G703 intro above table, detail below — each gated by its own bookmark, derived from
    // row data (same logic as the UI's auto-populate) when bookmarked with no saved value.
    const g703Intro = g703IntroField?.addToActionItems
      ? g703IntroField.value?.trim() || (rows.length > 0 ? generateG703IntroText(rows) : '')
      : '';
    const g703Detail = g703Field?.addToActionItems ? g703Field.value?.trim() || (rows.length > 0 ? generateG703DetailText(rows) : '') : '';
    if (g703Intro.trim()) {
      storedMaterialsHtml += toParagraphHtml(g703Intro);
      storedMaterialsOrder ??= g703IntroField?.actionItemOrder;
    }

    // Only include table when bookmarked (addToActionItems === true)
    if (rowsField?.addToActionItems && rows.length > 0) {
      const buildStoredMaterialsTable = (tableRowsData: StoredMaterialRow[]) => {
        const tableRows = tableRowsData
          .map(
            row =>
              `<tr><td style="${tdStyle}">${escapeHtml(String(row.description ?? ''))}</td><td style="${tdStyle}">${escapeHtml(
                String(row.storedOffsite ?? ''),
              )}</td><td style="${tdAmountStyle}">${(row.storedThisPeriod ?? 0).toLocaleString(undefined, {
                style: 'currency',
                currency: 'USD',
              })}</td></tr>`,
          )
          .join('');
        return `<table style="${tableStyle}"><thead><tr><th style="${thStyle}">Description</th><th style="${thStyle}">Stored Offsite?</th><th style="${thAmountStyle}">Amount</th></tr></thead><tbody>${tableRows}</tbody></table>`;
      };

      if (storedMaterialsHtml) storedMaterialsHtml += '<div style="margin: 0; height: 0.5em;"></div>';

      // Split per payapp — descriptions repeat across payapps, so a merged table would be ambiguous.
      const storedMaterialsDraws = (draws || []).filter(d => d.drawId);
      if (storedMaterialsDraws.length > 1) {
        const firstDrawId = storedMaterialsDraws[0].drawId;
        storedMaterialsDraws.forEach((draw, index) => {
          const drawRows = rows.filter(r => (r.drawId || firstDrawId) === draw.drawId);
          if (drawRows.length === 0) return;
          const label = draw.drawSummary?.name || `Draw ${index + 1}`;
          storedMaterialsHtml += `<p style="margin: 0.5em 0 0.25em 0;"><strong><em>${escapeHtml(label)} G703 Listed Materials:</em></strong></p>`;
          storedMaterialsHtml += buildStoredMaterialsTable(drawRows);
        });
      } else {
        storedMaterialsHtml += buildStoredMaterialsTable(rows);
      }
      storedMaterialsOrder ??= rowsField.actionItemOrder;
    }

    if (g703Detail.trim()) {
      if (storedMaterialsHtml) storedMaterialsHtml += '<div style="margin: 0; height: 0.5em;"></div>';
      storedMaterialsHtml += toParagraphHtml(g703Detail);
      storedMaterialsOrder ??= g703Field?.actionItemOrder;
    }
    if (otherListField?.addToActionItems && (otherListField.value || '').trim()) {
      if (storedMaterialsHtml) storedMaterialsHtml += '<div style="margin: 0; height: 0.5em;"></div>';
      storedMaterialsHtml += toParagraphHtml(otherListField.value);
      storedMaterialsOrder ??= otherListField.actionItemOrder;
    }

    // Stored Materials Comments (TextArray entries) - append as paragraphs at the end
    if (commentsField?.entries?.length) {
      commentsField.entries
        .filter(e => e.addToActionItems && (e.value || '').trim())
        .forEach(e => {
          const html = toParagraphHtml(e.value);
          if (!html) return;
          if (storedMaterialsHtml) storedMaterialsHtml += '<div style="margin: 0; height: 0.5em;"></div>';
          storedMaterialsHtml += html;
          storedMaterialsOrder ??= e.actionItemOrder;
        });
    }
  }

  // Plan/Cost Review Issues: text boxes + end comments
  const planCostReview: SectionFieldCollection | undefined = sections?.planCostReviewIssues;
  const planCostReviewTitle = 'Plan/Cost Review Issues';
  let planCostReviewHtml = '';
  let planCostReviewOrder: number | undefined;
  if (planCostReview?.fields?.length) {
    const primaryField = planCostReview.fields.find((f: SectionFieldDTO) => f.fieldName === 'PlanCostReviewPrimaryText');
    const secondaryField = planCostReview.fields.find((f: SectionFieldDTO) => f.fieldName === 'PlanCostReviewSecondaryText');
    const commentsField = planCostReview.fields.find((f: SectionFieldDTO) => f.fieldName === 'PlanCostReviewComments');

    if (primaryField?.addToActionItems && (primaryField.value || '').trim()) {
      planCostReviewHtml += toParagraphHtml(primaryField.value);
      planCostReviewOrder ??= primaryField.actionItemOrder;
    }
    if (secondaryField?.addToActionItems && (secondaryField.value || '').trim()) {
      if (planCostReviewHtml) planCostReviewHtml += '<div style="margin: 0; height: 0.5em;"></div>';
      planCostReviewHtml += toParagraphHtml(secondaryField.value);
      planCostReviewOrder ??= secondaryField.actionItemOrder;
    }

    // Plan/Cost Review Comments (TextArray entries) - append as paragraphs at the end
    if (commentsField?.entries?.length) {
      commentsField.entries
        .filter(e => e.addToActionItems && (e.value || '').trim())
        .forEach(e => {
          const html = toParagraphHtml(e.value);
          if (!html) return;
          if (planCostReviewHtml) planCostReviewHtml += '<div style="margin: 0; height: 0.5em;"></div>';
          planCostReviewHtml += html;
          planCostReviewOrder ??= e.actionItemOrder;
        });
    }
  }

  // Foundation Survey: text box + end comments
  const foundationSurvey: SectionFieldCollection | undefined = sections?.foundationSurvey;
  const foundationSurveyTitle = 'Foundation Survey';
  let foundationSurveyHtml = '';
  let foundationSurveyOrder: number | undefined;
  if (foundationSurvey?.fields?.length) {
    const contentField = foundationSurvey.fields.find((f: SectionFieldDTO) => f.fieldName === 'FoundationSurveyContent');
    const commentsField = foundationSurvey.fields.find((f: SectionFieldDTO) => f.fieldName === 'Comments');

    if (contentField?.addToActionItems && (contentField.value || '').trim()) {
      foundationSurveyHtml += toParagraphHtml(contentField.value);
      foundationSurveyOrder ??= contentField.actionItemOrder;
    }

    if (commentsField?.entries?.length) {
      commentsField.entries
        .filter(e => e.addToActionItems && (e.value || '').trim())
        .forEach(e => {
          const html = toParagraphHtml(e.value);
          if (!html) return;
          if (foundationSurveyHtml) foundationSurveyHtml += '<div style="margin: 0; height: 0.5em;"></div>';
          foundationSurveyHtml += html;
          foundationSurveyOrder ??= e.actionItemOrder;
        });
    }
  }

  // Progress Description: text box (Current Progress) + end comments
  const progressDescription: SectionFieldCollection | undefined = sections?.progressDescription;
  const progressDescriptionTitle = 'Progress Description';
  let progressDescriptionHtml = '';
  let progressDescriptionOrder: number | undefined;
  if (progressDescription?.fields?.length) {
    const currentProgressField = progressDescription.fields.find((f: SectionFieldDTO) => f.fieldName === 'CurrentProgress');
    const commentsField = progressDescription.fields.find((f: SectionFieldDTO) => f.fieldName === 'Comments');

    if (currentProgressField?.addToActionItems && (currentProgressField.value || '').trim()) {
      if (progressDescriptionHtml) progressDescriptionHtml += '<div style="margin: 0; height: 0.5em;"></div>';
      progressDescriptionHtml += toParagraphHtml(currentProgressField.value);
      progressDescriptionOrder ??= currentProgressField.actionItemOrder;
    }

    if (commentsField?.entries?.length) {
      commentsField.entries
        .filter(e => e.addToActionItems && (e.value || '').trim())
        .forEach(e => {
          const html = toParagraphHtml(e.value);
          if (!html) return;
          if (progressDescriptionHtml) progressDescriptionHtml += '<div style="margin: 0; height: 0.5em;"></div>';
          progressDescriptionHtml += html;
          progressDescriptionOrder ??= e.actionItemOrder;
        });
    }
  }

  // Minor Adjustments: text box (MinorAdjustmentsText) + end comments
  const minorAdjustments: SectionFieldCollection | undefined = sections?.minorAdjustments;
  const minorAdjustmentsTitle = 'Minor Adjustments';
  let minorAdjustmentsHtml = '';
  let minorAdjustmentsOrder: number | undefined;
  if (minorAdjustments?.fields?.length) {
    const textField = minorAdjustments.fields.find((f: SectionFieldDTO) => f.fieldName === 'MinorAdjustmentsText');
    const commentsField = minorAdjustments.fields.find((f: SectionFieldDTO) => f.fieldName === 'Comments');

    if (textField?.addToActionItems && (textField.value || '').trim()) {
      minorAdjustmentsHtml += toParagraphHtml(textField.value);
      minorAdjustmentsOrder ??= textField.actionItemOrder;

      // The boilerplate ends "we have made the adjustments' below", so the per-payapp tables have to
      // travel with it — bookmarking the text alone would leave "below" referring to nothing. The
      // amounts aren't section fields, so they come from each payapp's budget summary.
      const threshold = resolveMinorAdjustmentsThreshold(
        minorAdjustments.fields.find((f: SectionFieldDTO) => f.fieldName === 'Threshold')?.value,
      );
      const adjustments = (draws ?? []).map(d => ({
        drawId: d.drawId,
        name: d.drawSummary?.name || '',
        amount: Number(d.budgetSummary?.adjustmentsByCDCC) || 0,
      }));
      filterAdjustmentsAboveThreshold(adjustments, threshold).forEach(adjustment => {
        const label = adjustment.name ? `${adjustment.name} Minor Adjustments` : 'Minor Adjustments';
        minorAdjustmentsHtml +=
          `<p style="margin: 0.5em 0 0.25em 0;"><strong>${escapeHtml(label)}:</strong></p>` +
          `<p style="margin: 0;">CDCC Adjustments: ${escapeHtml(formatSignedCurrency(adjustment.amount))}</p>`;
      });
    }

    if (commentsField?.entries?.length) {
      commentsField.entries
        .filter(e => e.addToActionItems && (e.value || '').trim())
        .forEach(e => {
          const html = toParagraphHtml(e.value);
          if (!html) return;
          if (minorAdjustmentsHtml) minorAdjustmentsHtml += '<div style="margin: 0; height: 0.5em;"></div>';
          minorAdjustmentsHtml += html;
          minorAdjustmentsOrder ??= e.actionItemOrder;
        });
    }
  }

  // Documents Received: optional table (with intro line when bookmarked) + AllDocsComment, LoanDocsComment + Comments
  const DOCUMENTS_RECEIVED_INTRO =
    'We have received the following plans, specifications, and contracts in order to perform our review of the project.';
  const docsReceived: SectionFieldCollection | undefined = sections?.docsReceived;
  const docsReceivedTitle = 'Documents Received';
  let docsReceivedHtml = '';
  let docsReceivedOrder: number | undefined;
  if (docsReceived?.fields?.length && DOCUMENTS_RECEIVED_SECTION_CONFIG.layout) {
    const layout = DOCUMENTS_RECEIVED_SECTION_CONFIG.layout as { leftColumn?: FieldConfig[]; rightColumn?: FieldConfig[] };
    const leftCol = layout.leftColumn || [];
    const rightCol = layout.rightColumn || [];
    const getDocValue = (fieldName: string) => docsReceived.fields.find((f: SectionFieldDTO) => f.fieldName === fieldName)?.value || 'No';
    const isTableBookmarked = leftCol
      .concat(rightCol)
      .some((fc: FieldConfig) => docsReceived.fields.find((f: SectionFieldDTO) => f.fieldName === fc.fieldName)?.addToActionItems);

    if (isTableBookmarked) {
      docsReceivedHtml += toParagraphHtml(DOCUMENTS_RECEIVED_INTRO);
      const tableStyle = REPORT_TABLE_STYLE;
      const thStyle = 'border: none; padding: 4px 8px; font-weight: bold; text-align: left;';
      const tdStyle = 'border: none; padding: 4px 8px;';
      const maxRows = Math.max(leftCol.length, rightCol.length);
      const dataRows: string[] = [];
      for (let i = 0; i < maxRows; i++) {
        const leftC = leftCol[i] as FieldConfig | undefined;
        const rightC = rightCol[i] as FieldConfig | undefined;
        const leftLabel = leftC?.label ?? '';
        const leftVal = leftC ? getDocValue(leftC.fieldName) : '';
        const rightLabel = rightC?.label ?? '';
        const rightVal = rightC ? getDocValue(rightC.fieldName) : '';
        dataRows.push(
          `<tr><td style="${tdStyle}">${escapeHtml(leftLabel)}</td><td style="${tdStyle}">${escapeHtml(
            leftVal,
          )}</td><td style="${tdStyle}">${escapeHtml(rightLabel)}</td><td style="${tdStyle}">${escapeHtml(rightVal)}</td></tr>`,
        );
      }
      docsReceivedHtml += `<div style="margin: 0; height: 0.5em;"></div><table style="${tableStyle}"><thead><tr><th style="${thStyle}">Document</th><th style="${thStyle}">Received</th><th style="${thStyle}">Document</th><th style="${thStyle}">Received</th></tr></thead><tbody>${dataRows.join(
        '',
      )}</tbody></table>`;
    }

    const allDocsField = docsReceived.fields.find((f: SectionFieldDTO) => f.fieldName === 'AllDocsComment');
    const loanDocsField = docsReceived.fields.find((f: SectionFieldDTO) => f.fieldName === 'LoanDocsComment');
    const commentsField = docsReceived.fields.find((f: SectionFieldDTO) => f.fieldName === 'Comments');

    if (allDocsField?.addToActionItems && (allDocsField.value || '').trim()) {
      if (docsReceivedHtml) docsReceivedHtml += '<div style="margin: 0; height: 0.5em;"></div>';
      docsReceivedHtml += toParagraphHtml(allDocsField.value);
      docsReceivedOrder ??= allDocsField.actionItemOrder;
    }
    if (loanDocsField?.addToActionItems && (loanDocsField.value || '').trim()) {
      if (docsReceivedHtml) docsReceivedHtml += '<div style="margin: 0; height: 0.5em;"></div>';
      docsReceivedHtml += toParagraphHtml(loanDocsField.value);
      docsReceivedOrder ??= loanDocsField.actionItemOrder;
    }
    if (commentsField?.entries?.length) {
      commentsField.entries
        .filter(e => e.addToActionItems && (e.value || '').trim())
        .forEach(e => {
          const html = toParagraphHtml(e.value);
          if (!html) return;
          if (docsReceivedHtml) docsReceivedHtml += '<div style="margin: 0; height: 0.5em;"></div>';
          docsReceivedHtml += html;
          docsReceivedOrder ??= e.actionItemOrder;
        });
    }
  }

  // Material Testing Reports: text boxes (Auto, Failed Tests, All Pass, Recommendation comments) + end Comments
  const materialTesting: SectionFieldCollection | undefined = sections?.materialTestingReports;
  const materialTestingTitle = 'Materials Testing Reports';
  const MTR_AUTO_FIELD = 'MaterialTestingAutoComment';
  const MTR_FAILED_FIELD = 'MaterialTestingFailedTestsComment';
  const MTR_ALL_PASS_FIELD = 'MaterialTestingAllPassComment';
  const MTR_RECOMMENDATION_FIELD = 'MaterialTestingRecommendationComment';
  const MTR_PROVIDER_FIELD = 'MaterialTestingProviderComment';

  let materialTestingHtml = '';
  let materialTestingOrder: number | undefined;
  if (materialTesting?.fields?.length) {
    const autoField = materialTesting.fields.find((f: SectionFieldDTO) => f.fieldName === MTR_AUTO_FIELD);
    const failedField = materialTesting.fields.find((f: SectionFieldDTO) => f.fieldName === MTR_FAILED_FIELD);
    const allPassField = materialTesting.fields.find((f: SectionFieldDTO) => f.fieldName === MTR_ALL_PASS_FIELD);
    const recommendationField = materialTesting.fields.find((f: SectionFieldDTO) => f.fieldName === MTR_RECOMMENDATION_FIELD);
    const providerField = materialTesting.fields.find((f: SectionFieldDTO) => f.fieldName === MTR_PROVIDER_FIELD);
    const commentsField = materialTesting.fields.find((f: SectionFieldDTO) => f.fieldName === 'Comments');

    if (autoField?.addToActionItems && (autoField.value || '').trim()) {
      materialTestingHtml += toParagraphHtml(autoField.value);
      materialTestingOrder ??= autoField.actionItemOrder;
    }
    if (failedField?.addToActionItems && (failedField.value || '').trim()) {
      if (materialTestingHtml) materialTestingHtml += '<div style="margin: 0; height: 0.5em;"></div>';
      materialTestingHtml += toParagraphHtml(failedField.value);
      materialTestingOrder ??= failedField.actionItemOrder;

      const testsField = materialTesting.fields.find((f: SectionFieldDTO) => f.fieldName === 'MaterialTestingTests');
      const parsedTests: { testName?: string; failedTests?: string }[] = (testsField?.entries || [])
        .map(e => {
          try {
            return JSON.parse(e.value || '') as { testName?: string; failedTests?: string };
          } catch {
            return null;
          }
        })
        .filter((r): r is { testName?: string; failedTests?: string } => r != null);

      const splitFailedTestSegments = (failedTests?: string): string[] =>
        (failedTests || '')
          .split(';')
          .map(segment => segment.trim())
          .filter(segment => segment.length > 0);

      const failedRows: { testName?: string; segmentText: string }[] = parsedTests.flatMap(test =>
        splitFailedTestSegments(test.failedTests).map(segmentText => ({ testName: test.testName, segmentText })),
      );

      if (failedRows.length > 0) {
        const tableStyle = REPORT_TABLE_STYLE;
        const thStyle = 'border: none; padding: 4px 8px; font-weight: bold; text-align: left;';
        const tdStyle = 'border: none; padding: 4px 8px;';
        const rowsHtml = failedRows
          .map(
            row =>
              `<tr><td style="${tdStyle}">${escapeHtml(row.testName ?? '')}</td><td style="${tdStyle}">${escapeHtml(row.segmentText)}</td></tr>`,
          )
          .join('');
        materialTestingHtml += `<div style="margin: 0; height: 0.5em;"></div><table style="${tableStyle}"><thead><tr><th style="${thStyle}">Test Type</th><th style="${thStyle}">Failed Test No. / Information</th></tr></thead><tbody>${rowsHtml}</tbody></table>`;
      }
    }
    if (allPassField?.addToActionItems && (allPassField.value || '').trim()) {
      if (materialTestingHtml) materialTestingHtml += '<div style="margin: 0; height: 0.5em;"></div>';
      materialTestingHtml += toParagraphHtml(allPassField.value);
      materialTestingOrder ??= allPassField.actionItemOrder;
    }
    if (recommendationField?.addToActionItems && (recommendationField.value || '').trim()) {
      if (materialTestingHtml) materialTestingHtml += '<div style="margin: 0; height: 0.5em;"></div>';
      materialTestingHtml += toParagraphHtml(recommendationField.value);
      materialTestingOrder ??= recommendationField.actionItemOrder;
    }
    const neverReceived = allTestsNeverReceived(parseMaterialTestRows(materialTesting));

    if (!neverReceived && providerField?.addToActionItems && (providerField.value || '').trim()) {
      if (materialTestingHtml) materialTestingHtml += '<div style="margin: 0; height: 0.5em;"></div>';
      materialTestingHtml += toParagraphHtml(providerField.value);
      materialTestingOrder ??= providerField.actionItemOrder;
    }

    if (commentsField?.entries?.length) {
      commentsField.entries
        .filter(e => e.addToActionItems && (e.value || '').trim())
        .forEach(e => {
          const html = toParagraphHtml(e.value);
          if (!html) return;
          if (materialTestingHtml) materialTestingHtml += '<div style="margin: 0; height: 0.5em;"></div>';
          materialTestingHtml += html;
          materialTestingOrder ??= e.actionItemOrder;
        });
    }
  }

  // Construction Meeting Minutes: single free-text comment field
  const constructionMeetingMinutes: SectionFieldCollection | undefined = sections?.constructionMeetingMinutes;
  let constructionMeetingMinutesHtml = '';
  let constructionMeetingMinutesOrder: number | undefined;
  if (constructionMeetingMinutes?.fields?.length) {
    const commentField = constructionMeetingMinutes.fields.find(
      (f: SectionFieldDTO) => f.fieldName === 'ConstructionMeetingMinutesComment',
    );
    if (commentField?.addToActionItems && (commentField.value || '').trim()) {
      constructionMeetingMinutesHtml += toParagraphHtml(commentField.value);
      constructionMeetingMinutesOrder ??= commentField.actionItemOrder;
    }
  }

  // Previous Fundings: Comments (TextArray) entries only — the Note textbox has no bookmark of its own.
  const previousFundings: SectionFieldCollection | undefined =
    (sections as any)?.['previous-fundings'] ||
    sections?.previousFundings ||
    (sections as any)?.previousFunding ||
    (sections as any)?.previousFundingsSection;
  let previousFundingsHtml = '';
  let previousFundingsOrder: number | undefined;
  if (previousFundings?.fields?.length) {
    const noteField = previousFundings.fields.find((f: SectionFieldDTO) => f.fieldName === 'Note');
    const commentsField = previousFundings.fields.find((f: SectionFieldDTO) => f.fieldName === 'Comments');

    if (noteField?.addToActionItems && (previousFundingsComment || '').trim()) {
      previousFundingsHtml += toParagraphHtml(previousFundingsComment);
      previousFundingsOrder ??= noteField.actionItemOrder;
    }

    if (commentsField?.entries?.length) {
      commentsField.entries
        .filter(e => e.addToActionItems && (e.value || '').trim())
        .forEach(e => {
          const html = toParagraphHtml(e.value);
          if (!html) return;
          if (previousFundingsHtml) previousFundingsHtml += '<div style="margin: 0; height: 0.5em;"></div>';
          previousFundingsHtml += html;
          previousFundingsOrder ??= e.actionItemOrder;
        });
    }
  }

  // Retainage: text boxes (MainComment, ProceduresList, ReleaseProceduresList, ReleaseComments, LargestReleasesComment) + end Comments
  const retainage: SectionFieldCollection | undefined = sections?.retainage;
  let retainageHtml = '';
  let retainageOrder: number | undefined;
  if (retainage?.fields?.length) {
    const mainCommentField = retainage.fields.find((f: SectionFieldDTO) => f.fieldName === 'MainComment');
    const proceduresListField = retainage.fields.find((f: SectionFieldDTO) => f.fieldName === 'ProceduresList');
    const releaseProceduresListField = retainage.fields.find((f: SectionFieldDTO) => f.fieldName === 'ReleaseProceduresList');
    const releaseCommentsField = retainage.fields.find((f: SectionFieldDTO) => f.fieldName === 'ReleaseComments');
    const largestReleasesCommentField = retainage.fields.find((f: SectionFieldDTO) => f.fieldName === 'LargestReleasesComment');
    const commentsField = retainage.fields.find((f: SectionFieldDTO) => f.fieldName === 'Comments');

    if (mainCommentField?.addToActionItems && (mainCommentField.value || '').trim()) {
      retainageHtml += toParagraphHtml(mainCommentField.value);
      retainageOrder ??= mainCommentField.actionItemOrder;
    }
    if (proceduresListField?.addToActionItems && (proceduresListField.value || '').trim()) {
      if (retainageHtml) retainageHtml += '<div style="margin: 0; height: 0.5em;"></div>';
      retainageHtml += toParagraphHtml(proceduresListField.value);
      retainageOrder ??= proceduresListField.actionItemOrder;
    }
    if (releaseProceduresListField?.addToActionItems && (releaseProceduresListField.value || '').trim()) {
      if (retainageHtml) retainageHtml += '<div style="margin: 0; height: 0.5em;"></div>';
      retainageHtml += toParagraphHtml(releaseProceduresListField.value);
      retainageOrder ??= releaseProceduresListField.actionItemOrder;
    }
    if (releaseCommentsField?.addToActionItems && (releaseCommentsField.value || '').trim()) {
      if (retainageHtml) retainageHtml += '<div style="margin: 0; height: 0.5em;"></div>';
      retainageHtml += toParagraphHtml(releaseCommentsField.value);
      retainageOrder ??= releaseCommentsField.actionItemOrder;
    }
    if (largestReleasesCommentField?.addToActionItems && (largestReleasesCommentField.value || '').trim()) {
      if (retainageHtml) retainageHtml += '<div style="margin: 0; height: 0.5em;"></div>';
      retainageHtml += toParagraphHtml(largestReleasesCommentField.value);
      retainageOrder ??= largestReleasesCommentField.actionItemOrder;
    }

    if (commentsField?.entries?.length) {
      commentsField.entries
        .filter(e => e.addToActionItems && (e.value || '').trim())
        .forEach(e => {
          const html = toParagraphHtml(e.value);
          if (!html) return;
          if (retainageHtml) retainageHtml += '<div style="margin: 0; height: 0.5em;"></div>';
          retainageHtml += html;
          retainageOrder ??= e.actionItemOrder;
        });
    }
  }

  // Reallocations: table + Comment1 + Comment2 + Comments
  const reallocations: SectionFieldCollection | undefined = sections?.reallocations;
  const reallocationsTitle = 'Reallocations';
  const REALLOCATIONS_ROWS_FIELD = 'ReallocationsRows';
  const REALLOCATIONS_COMMENT1_FIELD = 'ReallocationsComment1';
  const REALLOCATIONS_COMMENT2_FIELD = 'ReallocationsComment2';

  let reallocationsHtml = '';
  let reallocationsOrder: number | undefined;
  if (reallocations?.fields?.length) {
    const rowsField = reallocations.fields.find((f: SectionFieldDTO) => f.fieldName === REALLOCATIONS_ROWS_FIELD);
    const comment1Field = reallocations.fields.find((f: SectionFieldDTO) => f.fieldName === REALLOCATIONS_COMMENT1_FIELD);
    const comment2Field = reallocations.fields.find((f: SectionFieldDTO) => f.fieldName === REALLOCATIONS_COMMENT2_FIELD);
    const commentsField = reallocations.fields.find((f: SectionFieldDTO) => f.fieldName === 'Comments');

    // Only rows with a nonzero Current Reallocation this period are shown - matching the
    // on-screen table, rows folded into the historical baseline are excluded here.
    const rows: ReallocationRowParsed[] = (rowsField?.entries || [])
      .map((e: FieldEntryDTO) => {
        try {
          return JSON.parse(e.value || '') as ReallocationRowParsed;
        } catch {
          return null;
        }
      })
      .filter((r): r is ReallocationRowParsed => r != null && (r.currentReallocation ?? 0) !== 0);

    // Only include tables when bookmarked
    if (rowsField?.addToActionItems && rows.length > 0) {
      const tableStyle = REPORT_TABLE_STYLE;
      const thStyle = 'border: none; padding: 4px 8px; font-weight: bold; text-align: left;';
      const thRightStyle = 'border: none; padding: 4px 8px; font-weight: bold; text-align: right;';
      const tdStyle = 'border: none; padding: 4px 8px;';
      const tdRightStyle = 'border: none; padding: 4px 8px; text-align: right;';

      /** One draw's table, with its own "Total Change Amounts" footer. */
      const buildReallocationsTable = (tableRowsData: ReallocationRowParsed[]) => {
        type TotalsType = {
          originalBudget: number;
          totalReallocation: number;
          currentReallocation: number;
          revisedBudget: number;
        };
        const totals: TotalsType = tableRowsData.reduce<TotalsType>(
          (acc, row) => {
            const originalBudget = row.originalBudget ?? 0;
            const totalReallocation = row.totalReallocation ?? 0;
            const currentReallocation = row.currentReallocation ?? 0;
            const revisedBudget = row.revisedBudget ?? originalBudget + currentReallocation;

            return {
              originalBudget: acc.originalBudget + originalBudget,
              totalReallocation: acc.totalReallocation + totalReallocation,
              currentReallocation: acc.currentReallocation + currentReallocation,
              revisedBudget: acc.revisedBudget + revisedBudget,
            };
          },
          { originalBudget: 0, totalReallocation: 0, currentReallocation: 0, revisedBudget: 0 },
        );

        const totalPercentChange = totals.originalBudget !== 0 ? (totals.currentReallocation / totals.originalBudget) * 100 : 0;

        const tableRows = tableRowsData
          .map(row => {
            const originalBudget = row.originalBudget ?? 0;
            const totalReallocation = row.totalReallocation ?? 0;
            const percentChange = row.percentChange ?? 0;
            const revisedBudget = row.revisedBudget ?? originalBudget + (row.currentReallocation ?? 0);
            const currentReallocation = row.currentReallocation ?? 0;

            return `<tr>
  <td style="${tdStyle}">${escapeHtml(String(row.description ?? ''))}</td>
  <td style="${tdRightStyle}">${formatCurrency(originalBudget.toString(), true)}</td>
  <td style="${tdRightStyle}">${formatCurrency(totalReallocation.toString(), true)}</td>
  <td style="${tdRightStyle}">${percentChange.toFixed(1)}%</td>
  <td style="${tdRightStyle}">${formatCurrency(revisedBudget.toString(), true)}</td>
  <td style="${tdRightStyle}">${formatCurrency(currentReallocation.toString(), true)}</td>
</tr>`;
          })
          .join('');

        const totalsRow = `<tr>
  <td style="${tdStyle}">Total Change Amounts:</td>
  <td style="${tdRightStyle}">${formatCurrency(totals.originalBudget.toString(), true)}</td>
  <td style="${tdRightStyle}">${formatCurrency(totals.totalReallocation.toString(), true)}</td>
  <td style="${tdRightStyle}">${totalPercentChange.toFixed(1)}%</td>
  <td style="${tdRightStyle}">${formatCurrency(totals.revisedBudget.toString(), true)}</td>
  <td style="${tdRightStyle}">${formatCurrency(totals.currentReallocation.toString(), true)}</td>
</tr>`;

        return `<table style="${tableStyle}"><thead><tr><th style="${thStyle}">Reallocated Line Item</th><th style="${thRightStyle}">Original Budget</th><th style="${thRightStyle}">Total Reallocations</th><th style="${thRightStyle}">% Change</th><th style="${thRightStyle}">Revised Budget from Reallocations</th><th style="${thRightStyle}">Current Reallocation</th></tr></thead><tbody>${tableRows}</tbody><tfoot>${totalsRow}</tfoot></table>`;
      };

      // Split by G702: one labelled table per covered draw; a single draw renders as before.
      const reallocationDraws = (draws || []).filter(d => d.drawId);
      if (reallocationDraws.length > 1) {
        const firstDrawId = reallocationDraws[0].drawId;
        reallocationDraws.forEach((draw, index) => {
          const drawRows = rows.filter(r => (r.drawId || firstDrawId) === draw.drawId);
          if (drawRows.length === 0) return;
          const label = draw.drawSummary?.name || `Draw ${index + 1}`;
          reallocationsHtml += `<p style="margin: 0.5em 0 0.25em 0;"><strong><em>${escapeHtml(label)} Reallocated Line Items:</em></strong></p>`;
          reallocationsHtml += buildReallocationsTable(drawRows);
        });
      } else {
        reallocationsHtml += buildReallocationsTable(rows);
      }
      reallocationsOrder ??= rowsField.actionItemOrder;
    }

    // ReallocationsComment1 text
    if (comment1Field?.addToActionItems && (comment1Field.value || '').trim()) {
      if (reallocationsHtml) reallocationsHtml += '<div style="margin: 0; height: 0.5em;"></div>';
      reallocationsHtml += toParagraphHtml(comment1Field.value);
      reallocationsOrder ??= comment1Field.actionItemOrder;
    }

    // ReallocationsComment2 text
    if (comment2Field?.addToActionItems && (comment2Field.value || '').trim()) {
      if (reallocationsHtml) reallocationsHtml += '<div style="margin: 0; height: 0.5em;"></div>';
      reallocationsHtml += toParagraphHtml(comment2Field.value);
      reallocationsOrder ??= comment2Field.actionItemOrder;
    }

    // Comments entries at the end
    if (commentsField?.entries?.length) {
      commentsField.entries
        .filter(e => e.addToActionItems && (e.value || '').trim())
        .forEach(e => {
          const html = toParagraphHtml(e.value);
          if (!html) return;
          if (reallocationsHtml) reallocationsHtml += '<div style="margin: 0; height: 0.5em;"></div>';
          reallocationsHtml += html;
          reallocationsOrder ??= e.actionItemOrder;
        });
    }
  }

  // Change Orders: table (last month + current, with current in bold) + Comment1 + Comment2 + Comments
  const changeOrders: SectionFieldCollection | undefined = sections?.changeOrders;
  const changeOrdersTitle = 'Change Orders';
  const CHANGE_ORDERS_ROWS_FIELD = 'ChangeOrdersRows';
  const CHANGE_ORDERS_COMMENT1_FIELD = 'ChangeOrdersComment1';
  const CHANGE_ORDERS_COMMENT2_FIELD = 'ChangeOrdersComment2';

  let changeOrdersHtml = '';
  let changeOrdersOrder: number | undefined;
  if (changeOrders?.fields?.length) {
    const rowsField = changeOrders.fields.find((f: SectionFieldDTO) => f.fieldName === CHANGE_ORDERS_ROWS_FIELD);
    const comment1Field = changeOrders.fields.find((f: SectionFieldDTO) => f.fieldName === CHANGE_ORDERS_COMMENT1_FIELD);
    const comment2Field = changeOrders.fields.find((f: SectionFieldDTO) => f.fieldName === CHANGE_ORDERS_COMMENT2_FIELD);
    const commentsField = changeOrders.fields.find((f: SectionFieldDTO) => f.fieldName === 'Comments');

    // ChangeOrdersComment1 text
    if (comment1Field?.addToActionItems && (comment1Field.value || '').trim()) {
      if (changeOrdersHtml) changeOrdersHtml += '<div style="margin: 0; height: 0.5em;"></div>';
      changeOrdersHtml += toParagraphHtml(comment1Field.value);
      changeOrdersOrder ??= comment1Field.actionItemOrder;
    }

    // ChangeOrdersComment2 text
    if (comment2Field?.addToActionItems && (comment2Field.value || '').trim()) {
      if (changeOrdersHtml) changeOrdersHtml += '<div style="margin: 0; height: 0.5em;"></div>';
      changeOrdersHtml += toParagraphHtml(comment2Field.value);
      changeOrdersOrder ??= comment2Field.actionItemOrder;
    }

    // Comments entries at the end
    if (commentsField?.entries?.length) {
      commentsField.entries
        .filter(e => e.addToActionItems && (e.value || '').trim())
        .forEach(e => {
          const html = toParagraphHtml(e.value);
          if (!html) return;
          if (changeOrdersHtml) changeOrdersHtml += '<div style="margin: 0; height: 0.5em;"></div>';
          changeOrdersHtml += html;
          changeOrdersOrder ??= e.actionItemOrder;
        });
    }

    const rows: ChangeOrderRowParsed[] = (rowsField?.entries || [])
      .map((e: FieldEntryDTO) => {
        try {
          return JSON.parse(e.value || '') as ChangeOrderRowParsed;
        } catch {
          return null;
        }
      })
      .filter((r): r is ChangeOrderRowParsed => r != null);

    // Only include table when bookmarked
    if (rowsField?.addToActionItems && rows.length > 0) {
      const tableStyle = REPORT_TABLE_STYLE;
      const thStyle = 'border: none; padding: 4px 8px; font-weight: bold; text-align: left;';
      const thCenterStyle = 'border: none; padding: 4px 8px; font-weight: bold; text-align: center;';
      const thRightStyle = 'border: none; padding: 4px 8px; font-weight: bold; text-align: right;';
      const tdStyle = 'border: none; padding: 4px 8px;';
      const tdCenterStyle = 'border: none; padding: 4px 8px; text-align: center;';
      const tdRightStyle = 'border: none; padding: 4px 8px; text-align: right;';

      // Note above the table, matching Excel note
      changeOrdersHtml += `<p style="margin: 0 0 0.5em 0;"><em>(New Change Orders in bold font:)</em></p>`;

      const buildRowsTable = (tableRowsData: ChangeOrderRowParsed[]) => {
        const tableRows = tableRowsData
          .map(row => {
            const isCurrent = !row.isLastMonth;
            const wrapStart = isCurrent ? '<strong>' : '';
            const wrapEnd = isCurrent ? '</strong>' : '';

            const coNumber = row.coNumber ?? '';
            const description = escapeHtml(String(row.description ?? ''));
            const docValue = (row.doWeHaveDoc || '').toString();
            const contractor = (row.contractor || '').toString();
            const architect = (row.architect || '').toString();
            const owner = (row.owner || '').toString();
            const prevCOAmount = row.prevCO ?? (row as any).newCO ?? 0;
            const currCOAmount = row.currCO ?? 0;

            return `<tr>
  <td style="${tdCenterStyle}">${wrapStart}${coNumber}${wrapEnd}</td>
  <td style="${tdStyle}">${wrapStart}${description}${wrapEnd}</td>
  <td style="${tdCenterStyle}">${wrapStart}${escapeHtml(docValue)}${wrapEnd}</td>
  <td style="${tdCenterStyle}">${wrapStart}${escapeHtml(contractor)}${wrapEnd}</td>
  <td style="${tdCenterStyle}">${wrapStart}${escapeHtml(architect)}${wrapEnd}</td>
  <td style="${tdCenterStyle}">${wrapStart}${escapeHtml(owner)}${wrapEnd}</td>
  <td style="${tdRightStyle}">${wrapStart}${formatCurrency(prevCOAmount.toString(), true)}${wrapEnd}</td>
  <td style="${tdRightStyle}">${wrapStart}${formatCurrency(currCOAmount.toString(), true)}${wrapEnd}</td>
</tr>`;
          })
          .join('');

        return `<table style="${tableStyle}"><thead><tr><th style="${thCenterStyle}">C.O. #</th><th style="${thStyle}">Description of the Changes in Scope of Work</th><th style="${thCenterStyle}">Docmt. Rec'd.</th><th style="${thCenterStyle}">Signatures: Contr.</th><th style="${thCenterStyle}">Signatures: Arch.</th><th style="${thCenterStyle}">Signatures: Owner</th><th style="${thRightStyle}">Prev CO</th><th style="${thRightStyle}">Curr CO</th></tr></thead><tbody>${tableRows}</tbody></table>`;
      };

      // Summary table below each Change Orders table. Per-draw fields with a fallback to the
      // un-suffixed originals (single-draw reports and data saved before change orders went per-draw).
      const buildSummaryTable = (drawId?: string) => {
        const fieldValue = (base: string) => {
          const scoped = drawId ? changeOrders.fields.find((f: SectionFieldDTO) => f.fieldName === `${base}__${drawId}`)?.value : undefined;
          return scoped || changeOrders.fields.find((f: SectionFieldDTO) => f.fieldName === base)?.value || '';
        };

        const originalContractSum = fieldValue('ChangeOrdersSummaryOriginalContractSum');
        const totalNetCO = fieldValue('ChangeOrdersSummaryTotalNetChangeOrder');
        const totalNetRealloc = fieldValue('ChangeOrdersSummaryTotalNetReallocation');
        const contractSumToDate = fieldValue('ChangeOrdersSummaryContractSumToDate');

        if (!originalContractSum && !totalNetCO && !totalNetRealloc && !contractSumToDate) return '';

        const summaryTableStyle = `border-collapse: collapse; margin: 0.5em 0 0.5em 0; width: 100%; border: none; font-family: ${REPORT_FONT_FAMILY}; font-size: ${REPORT_TABLE_FONT_SIZE};`;
        const summaryLabelTdStyle = 'border: none; padding: 4px 8px; text-align: left;';
        const summaryValueTdStyle = 'border: none; padding: 4px 8px; text-align: right;';

        const safeFormat = (raw: string) => {
          const trimmed = (raw || '').trim();
          if (!trimmed) return '';
          const n = Number(trimmed);
          if (Number.isNaN(n)) return escapeHtml(trimmed);
          return formatCurrency(n.toString(), true);
        };

        const summaryRows = [
          ['Original Contract Sum:', originalContractSum],
          ['Total Net Change Order Amount:', totalNetCO],
          ['Total Net Reallocation Amount:', totalNetRealloc],
          ['Contract Sum To Date:', contractSumToDate],
        ].map(
          ([label, value]) =>
            `<tr><td style="${summaryLabelTdStyle}">${label}</td><td style="${summaryValueTdStyle}">${safeFormat(value)}</td></tr>`,
        );

        return `<table style="${summaryTableStyle}"><tbody>${summaryRows.join('')}</tbody></table>`;
      };

      // One labelled table + summary per covered draw that isn't hidden; a single draw renders as
      // it always did. Legacy rows with no drawId still anchor to the first COVERED draw, so that
      // anchor is taken before the hidden ones are filtered out.
      const coveredChangeOrderDraws = (draws || []).filter(d => d.drawId);
      const firstCoveredDrawId = coveredChangeOrderDraws[0]?.drawId;
      const changeOrderDraws = filterVisibleCoLienDraws(coveredChangeOrderDraws, coLienHidden);
      const visibleRows = rows.filter(r => !isCoLienHidden(coLienHidden, r.drawId || firstCoveredDrawId));
      if (changeOrderDraws.length > 1) {
        changeOrderDraws.forEach((draw, index) => {
          const drawRows = visibleRows.filter(r => (r.drawId || firstCoveredDrawId) === draw.drawId);
          if (drawRows.length === 0) return;
          const label = draw.drawSummary?.name || `Draw ${index + 1}`;
          changeOrdersHtml += `<p style="margin: 0.5em 0 0.25em 0;"><strong><em>${escapeHtml(label)}</em></strong></p>`;
          changeOrdersHtml += buildRowsTable(drawRows);
          changeOrdersHtml += buildSummaryTable(draw.drawId);
        });
      } else {
        changeOrdersHtml += buildRowsTable(visibleRows);
        changeOrdersHtml += buildSummaryTable(changeOrderDraws[0]?.drawId);
      }
    }
  }

  // Potential Change Orders: single free-text comment field
  const potentialChangeOrders: SectionFieldCollection | undefined = sections?.potentialChangeOrders;
  let potentialChangeOrdersHtml = '';
  let potentialChangeOrdersOrder: number | undefined;
  if (potentialChangeOrders?.fields?.length) {
    const commentField = potentialChangeOrders.fields.find((f: SectionFieldDTO) => f.fieldName === 'PotentialChangeOrdersComment');
    if (commentField?.addToActionItems && (commentField.value || '').trim()) {
      potentialChangeOrdersHtml += toParagraphHtml(commentField.value);
      potentialChangeOrdersOrder ??= commentField.actionItemOrder;
    }
  }

  // Lien Waivers: intro comment + the single generated comment covering every payapp, + Comments.
  // Both are status-independent now — the generated comment states each payapp's own shortfall, so
  // there is no longer an Unconditional-only path. The below-table comment is retired.
  const lienWaivers: SectionFieldCollection | undefined = sections?.lienWaivers;
  const lienWaiversTitle = 'Lien Waivers';

  let lienWaiversHtml = '';
  let lienWaiversOrder: number | undefined;
  if (lienWaivers?.fields?.length) {
    const mainCommentField = lienWaivers.fields.find((f: SectionFieldDTO) => f.fieldName === 'LienWaiverComment');
    const aboveTableField = lienWaivers.fields.find((f: SectionFieldDTO) => f.fieldName === 'LienWaiverAboveTableComment');
    const belowTableField = lienWaivers.fields.find((f: SectionFieldDTO) => f.fieldName === 'LienWaiverBelowTableComment');
    const commentsField = lienWaivers.fields.find((f: SectionFieldDTO) => f.fieldName === 'Comments');

    if (aboveTableField?.addToActionItems && (aboveTableField.value || '').trim()) {
      lienWaiversHtml += toParagraphHtml(aboveTableField.value);
      lienWaiversOrder ??= aboveTableField.actionItemOrder;
    }

    if (mainCommentField?.addToActionItems && (mainCommentField.value || '').trim()) {
      if (lienWaiversHtml) lienWaiversHtml += '<div style="margin: 0; height: 0.5em;"></div>';
      lienWaiversHtml += toParagraphHtml(mainCommentField.value);
      lienWaiversOrder ??= mainCommentField.actionItemOrder;
    }

    // Retained only for reports whose section predates multi-draw and still holds text here.
    const hasDrawWaivers = (lienWaivers.fields.find((f: SectionFieldDTO) => f.fieldName === 'LienWaiverDraws')?.value || '[]') !== '[]';
    if (!hasDrawWaivers && belowTableField?.addToActionItems && (belowTableField.value || '').trim()) {
      if (lienWaiversHtml) lienWaiversHtml += '<div style="margin: 0; height: 0.5em;"></div>';
      lienWaiversHtml += toParagraphHtml(belowTableField.value);
      lienWaiversOrder ??= belowTableField.actionItemOrder;
    }

    // Lien Waivers Comments (TextArray)
    if (commentsField?.entries?.length) {
      commentsField.entries
        .filter(e => e.addToActionItems && (e.value || '').trim())
        .forEach(e => {
          const html = toParagraphHtml(e.value);
          if (!html) return;
          if (lienWaiversHtml) lienWaiversHtml += '<div style="margin: 0; height: 0.5em;"></div>';
          lienWaiversHtml += html;
          lienWaiversOrder ??= e.actionItemOrder;
        });
    }
  }

  // Reduction Comments: table + No Work Comments + Reduction Comments + Comments.
  // Draw-scoped — one copy per covered draw, labelled when more than one contributes.
  const reductionCommentsTitle = 'Reduction Comments';
  const REDUCTION_ROWS_FIELD = 'ReductionCommentsRows';
  const ZERO_WORK_FIELD = 'ZeroWorkComments';
  const PARTIAL_REDUCTION_FIELD = 'PartialReductionComments';

  let reductionCommentsHtml = '';
  let reductionCommentsOrder: number | undefined;

  const buildReductionCommentsBody = (reductionComments?: SectionFieldCollection): string => {
    let body = '';
    if (!reductionComments?.fields?.length) return body;
    const rowsField = reductionComments.fields.find((f: SectionFieldDTO) => f.fieldName === REDUCTION_ROWS_FIELD);
    const zeroWorkField = reductionComments.fields.find((f: SectionFieldDTO) => f.fieldName === ZERO_WORK_FIELD);
    const partialReductionField = reductionComments.fields.find((f: SectionFieldDTO) => f.fieldName === PARTIAL_REDUCTION_FIELD);
    const commentsField = reductionComments.fields.find((f: SectionFieldDTO) => f.fieldName === 'Comments');

    const rows: ReductionRowParsed[] = (rowsField?.entries || [])
      .map((e: FieldEntryDTO) => {
        try {
          return JSON.parse(e.value || '') as ReductionRowParsed;
        } catch {
          return null;
        }
      })
      .filter((r): r is ReductionRowParsed => r != null);

    // Only include table when bookmarked
    if (rowsField?.addToActionItems && rows.length > 0) {
      const tableStyle = REPORT_TABLE_STYLE;
      const thStyle = 'border: none; padding: 4px 8px; font-weight: bold; text-align: left;';
      const thRightStyle = 'border: none; padding: 4px 8px; font-weight: bold; text-align: right;';
      const tdStyle = 'border: none; padding: 4px 8px;';
      const tdRightStyle = 'border: none; padding: 4px 8px; text-align: right;';

      const tableRows = rows
        .map(row => {
          const conditionLabel =
            row.conditionType === 'no-work' ? 'No Work' : row.conditionType === 'partial' ? 'Partial' : row.conditionType || '';
          const requested = row.requestedAmount ?? 0;
          const funding = row.fundingAmount ?? 0;
          const reducedBy = row.reducedBy ?? requested - funding;
          const percentRequested = row.percentRequested ?? 0;
          const percentRecommended = row.percentRecommended ?? 0;

          return `<tr>
  <td style="${tdStyle}">${escapeHtml(String(row.description ?? ''))}</td>
  <td style="${tdStyle}">${escapeHtml(conditionLabel)}</td>
  <td style="${tdRightStyle}">${formatCurrency(requested.toString(), true)}</td>
  <td style="${tdRightStyle}">${formatCurrency(funding.toString(), true)}</td>
  <td style="${tdRightStyle}">${formatCurrency(reducedBy.toString(), true)}</td>
  <td style="${tdRightStyle}">${Math.round(percentRequested)}%</td>
  <td style="${tdRightStyle}">${roundRecommendedPercent(percentRecommended, percentRequested)}%</td>
</tr>`;
        })
        .join('');

      body += `<table style="${tableStyle}"><thead><tr><th style="${thStyle}">Line Item</th><th style="${thStyle}">Condition</th><th style="${thRightStyle}">Requested</th><th style="${thRightStyle}">Recommended</th><th style="${thRightStyle}">Reduced By</th><th style="${thRightStyle}">% Requested</th><th style="${thRightStyle}">% Recommended</th></tr></thead><tbody>${tableRows}</tbody></table>`;
      reductionCommentsOrder ??= rowsField.actionItemOrder;
    }

    // Zero Work Comments text
    if (zeroWorkField?.addToActionItems && (zeroWorkField.value || '').trim()) {
      if (body) body += '<div style="margin: 0; height: 0.5em;"></div>';
      body += toParagraphHtml(zeroWorkField.value);
      reductionCommentsOrder ??= zeroWorkField.actionItemOrder;
    }

    // Partial Reduction Comments text
    if (partialReductionField?.addToActionItems && (partialReductionField.value || '').trim()) {
      if (body) body += '<div style="margin: 0; height: 0.5em;"></div>';
      body += toParagraphHtml(partialReductionField.value);
      reductionCommentsOrder ??= partialReductionField.actionItemOrder;
    }

    // Comments entries at the end
    if (commentsField?.entries?.length) {
      commentsField.entries
        .filter(e => e.addToActionItems && (e.value || '').trim())
        .forEach(e => {
          const html = toParagraphHtml(e.value);
          if (!html) return;
          if (body) body += '<div style="margin: 0; height: 0.5em;"></div>';
          body += html;
          reductionCommentsOrder ??= e.actionItemOrder;
        });
    }

    return body;
  };

  {
    const perDraw = collectDrawScopedSections(sections, 'reduction-comments', drawOrder);
    if (perDraw.length > 0) {
      const bodies = perDraw
        .map(({ drawId, collection }) => ({ label: drawLabels?.[drawId], body: buildReductionCommentsBody(collection) }))
        .filter(entry => entry.body);
      const showLabels = bodies.length > 1;
      reductionCommentsHtml = bodies
        .map(entry => (showLabels && entry.label ? `${drawLabelHtml(entry.label)}${entry.body}` : entry.body))
        .join('<div style="margin: 0; height: 0.5em;"></div>');
    } else {
      // Legacy report saved before draw-scoping.
      reductionCommentsHtml = buildReductionCommentsBody((sections as any)?.['reduction-comments'] || (sections as any)?.reductionComments);
    }
  }

  type Bullet = { body: string; order: number };
  const bullets: Bullet[] = [];
  let fallbackIndex = 0;

  if (applicationCommentsHtml) {
    bullets.push({ body: applicationCommentsHtml, order: applicationCommentsOrder ?? LARGE_ORDER_FALLBACK + fallbackIndex++ });
  }
  if (reductionCommentsHtml) {
    bullets.push({ body: reductionCommentsHtml, order: reductionCommentsOrder ?? LARGE_ORDER_FALLBACK + fallbackIndex++ });
  }
  if (notaryHtml) {
    bullets.push({ body: notaryHtml, order: notaryOrder ?? LARGE_ORDER_FALLBACK + fallbackIndex++ });
  }
  if (scheduleHtml) {
    bullets.push({ body: scheduleHtml, order: scheduleOrder ?? LARGE_ORDER_FALLBACK + fallbackIndex++ });
  }
  if (storedMaterialsHtml) {
    bullets.push({ body: storedMaterialsHtml, order: storedMaterialsOrder ?? LARGE_ORDER_FALLBACK + fallbackIndex++ });
  }
  if (planCostReviewHtml) {
    bullets.push({ body: planCostReviewHtml, order: planCostReviewOrder ?? LARGE_ORDER_FALLBACK + fallbackIndex++ });
  }
  if (foundationSurveyHtml) {
    bullets.push({ body: foundationSurveyHtml, order: foundationSurveyOrder ?? LARGE_ORDER_FALLBACK + fallbackIndex++ });
  }
  if (progressDescriptionHtml) {
    bullets.push({ body: progressDescriptionHtml, order: progressDescriptionOrder ?? LARGE_ORDER_FALLBACK + fallbackIndex++ });
  }
  if (minorAdjustmentsHtml) {
    bullets.push({ body: minorAdjustmentsHtml, order: minorAdjustmentsOrder ?? LARGE_ORDER_FALLBACK + fallbackIndex++ });
  }
  if (docsReceivedHtml) {
    bullets.push({ body: docsReceivedHtml, order: docsReceivedOrder ?? LARGE_ORDER_FALLBACK + fallbackIndex++ });
  }
  if (materialTestingHtml) {
    bullets.push({ body: materialTestingHtml, order: materialTestingOrder ?? LARGE_ORDER_FALLBACK + fallbackIndex++ });
  }
  if (constructionMeetingMinutesHtml) {
    bullets.push({
      body: constructionMeetingMinutesHtml,
      order: constructionMeetingMinutesOrder ?? LARGE_ORDER_FALLBACK + fallbackIndex++,
    });
  }
  if (previousFundingsHtml) {
    bullets.push({ body: previousFundingsHtml, order: previousFundingsOrder ?? LARGE_ORDER_FALLBACK + fallbackIndex++ });
  }
  if (retainageHtml) {
    bullets.push({ body: retainageHtml, order: retainageOrder ?? LARGE_ORDER_FALLBACK + fallbackIndex++ });
  }
  if (reallocationsHtml) {
    bullets.push({ body: reallocationsHtml, order: reallocationsOrder ?? LARGE_ORDER_FALLBACK + fallbackIndex++ });
  }
  if (changeOrdersHtml) {
    bullets.push({ body: changeOrdersHtml, order: changeOrdersOrder ?? LARGE_ORDER_FALLBACK + fallbackIndex++ });
  }
  if (potentialChangeOrdersHtml) {
    bullets.push({ body: potentialChangeOrdersHtml, order: potentialChangeOrdersOrder ?? LARGE_ORDER_FALLBACK + fallbackIndex++ });
  }
  if (lienWaiversHtml) {
    bullets.push({ body: lienWaiversHtml, order: lienWaiversOrder ?? LARGE_ORDER_FALLBACK + fallbackIndex++ });
  }

  // Dynamic sections: generic header/body + comments, driven by bookmarks
  const dynamicSections: any[] = Array.isArray(sections?.dynamicSections) ? sections.dynamicSections : [];
  dynamicSections.forEach((section: SectionFieldCollection & { insertAfterSectionId?: string }, index: number) => {
    const fields = section?.fields || [];
    if (!fields.length) return;

    const headerField = fields.find((f: SectionFieldDTO) => f.fieldName === 'header');
    const bodyField = fields.find((f: SectionFieldDTO) => f.fieldName === 'body');
    const commentsField = fields.find((f: SectionFieldDTO) => f.fieldName === 'Comments');

    const rawTitle = (headerField?.value || '').toString().trim();
    const title = rawTitle || `Section ${index + 1}`;

    let sectionHtml = '';
    let dynamicSectionOrder: number | undefined;

    // Body text when bookmarked
    if (bodyField?.addToActionItems && (bodyField.value || '').trim()) {
      sectionHtml += toParagraphHtml(bodyField.value);
      dynamicSectionOrder ??= bodyField.actionItemOrder;
    }

    // Comments entries when bookmarked
    if (commentsField?.entries?.length) {
      commentsField.entries
        .filter(e => e.addToActionItems && (e.value || '').trim())
        .forEach(e => {
          const html = toParagraphHtml(e.value);
          if (!html) return;
          if (sectionHtml) sectionHtml += '<div style="margin: 0; height: 0.5em;"></div>';
          sectionHtml += html;
          dynamicSectionOrder ??= e.actionItemOrder;
        });
    }

    if (sectionHtml) {
      bullets.push({ body: sectionHtml, order: dynamicSectionOrder ?? LARGE_ORDER_FALLBACK + fallbackIndex++ });
    }
  });

  // Sort bullets by earliest bookmark order so that the first-clicked
  // section appears as item 1, second as item 2, etc.
  bullets.sort((a, b) => a.order - b.order);

  const buildItem = (body: string) =>
    `<li style="margin-bottom: 1em; font-weight: bold;"><span style="font-weight: normal;">${body}</span></li>`;

  const numberedItems =
    bullets.length > 0
      ? `<ol class="action-items-list" style="margin: 0; padding-left: 0;">${bullets.map(b => buildItem(b.body)).join('')}</ol>`
      : `<ol class="action-items-list" style="margin: 0; padding-left: 0;">${buildItem(
          '<p style="margin: 0 0 0.25em 0; display: inline;">[Add action items]</p>',
        )}</ol>`;

  return `${baseWrapperStart}${header}${intro}${numberedItems}</div>`;
}
